import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import LabelSignatureStatus from 'components/molecules/labeledStatus/LabeledSignature';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IAdvance, SIGNATURE_TYPE } from 'services/advance/types';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { getDefaultCurrency } from 'store/state';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import * as Yup from 'yup';
import { IFormStatus } from '../types';
import AdvanceContracts from './tabs/AdvanceContracts';
import RepaymentsTable from './tabs/RepaymentsTable';

export enum LabelTabs {
  repayments,
  contracts,
}

interface RepaymentsFromAllContracts {
  value: boolean | string;
  label: string;
}

interface AdvanceLabelFormProps {
  initialValues?: IAdvance;
  i18nextPrefix?: string;
  isPending?: boolean;
  title?: string;
  onSubmit?: (values: any) => void;
  displayMode?: boolean;
  disable?: boolean;
  topbarContent?: React.ReactNode;
  backPath?: string;
  customBackItem?: React.ReactNode;
  initialStatus?: any;
  disableTabs?: any;
  status: IFormStatus;
}

export interface IRepaymentDocument {
  number: {
    shortcut: string;
    number: number;
    month: number;
    year: number;
    value: string;
  };
  id: string;
  fileName: string;
  path: string;
  sizeInMB: number;
  createdAt: string;
  extension: string;
  mD5Hash: string;
}

export interface IRepayment {
  amount: string | number;
  repaymentDate: string;
  settlementId: string | null;
  repaymentSource: {
    name: string;
    id: number;
  };
  repaymentSourceId: string;
  repaymentDocument: IRepaymentDocument;
  isPaidFromNet: boolean;
}

export interface IDocumentNumber {
  shortcut: string;
  number: number;
  month: number;
  year: number;
  value: string;
}

export interface Repayment {
  id: string;
  settlementId: string;
  date: string;
  amount: string | number;
  remainingNetAmount: number;
  description: string;
  forSignature: boolean;
  repaymentsFromAllContracts: boolean | null;
  proposalAdvanceDocumentId: string;
  proposalAdvanceDocumentPath: string;
  finalAdvanceDocumentId: string;
  finalAdvanceDocumentPath: string;
  proposalNumber: number;
  proposalMonth: number;
  proposalYear: number;
  finalNumber: number;
  finalMonth: number;
  finalYear: number;
  maximumRepaymentPercentage: number | null;
  repayments: IRepayment[];
  contracts: any[];
  proposalDocumentNumber: { documentId: string; number: number };
  finalDocumentNumber: { documentId: string; number: number };
}

export const advanceLableInitialValue = {
  id: '',
  settlementId: '',
  settlementNumber: null,
  date: '',
  amount: null,
  remainingNetAmount: 0,
  advanceNumber: '',
  description: '',
  forSignature: '',
  repaymentsFromAllContracts: true,
  proposalAdvanceDocumentPath: '',
  finalAdvanceDocumentPath: '',
  maximumRepaymentPercentage: null,
  repayments: [],
  contracts: [],
  settlementPeriodId: '',
  settlementPeriod: null,
  finalDocumentNumber: '',
  statusId: false,
  version: 0,
  isSettled: false,
};

const AdvanceLabelForm = ({
  i18nextPrefix,
  isPending,
  disable,
  topbarContent,
  title,
  displayMode = false,
  initialValues = advanceLableInitialValue,
  backPath,
  customBackItem,
  status,
  onSubmit,
  disableTabs,
}: AdvanceLabelFormProps): JSX.Element => {
  const { t } = useTranslation();

  const contractValidation = Yup.object().shape({
    id: Yup.string().required(() => t('formik-error.required')),
  });

  const validationSchema = Yup.object().shape({
    description: Yup.string().required(t('formik-error.required')),
    statusId: Yup.string().required(t('formik-error.required')),
    settlementPeriodId: Yup.string().required(t('formik-error.required')),
    maximumRepaymentPercentage: Yup.number()
      .required(t('formik-error.required'))
      .positive(t('formik-error.must-be-at-least'))
      .max(100, t('formik-error.max-percent-value', { value: 100 })),

    amount: Yup.number()
      .required(t('formik-error.required'))
      .positive(t('formik-error.amount-must-be-positive')),

    contracts: Yup.array()
      .of(contractValidation)
      .when('repaymentsFromAllContracts', {
        is: false,
        then: schema => schema.min(1, t('formik-error.contracts-required')),
        otherwise: schema => schema.notRequired(),
      }),
  });

  const [selectedOptions, setSelectedOptions] = useState<
    RepaymentsFromAllContracts[]
  >([]);

  const isContractSigned = [
    { value: true, label: t(`enum.LABEL_ADVANCE.signed`) },
    { value: false, label: t(`enum.LABEL_ADVANCE.unsigned`) },
  ];

  const isRepaymentsFromAllContracts = [
    { value: true, label: t(`enum.LABEL_REPAYMENTS.Yes`) },
    { value: false, label: t(`enum.LABEL_REPAYMENTS.No`) },
  ];

  const defaultCurrency = getDefaultCurrency()?.currencyCode ?? '';

  const settlementPeriods = useGetSettlementLightList();
  const settlementPeriodOptions =
    settlementPeriods.data?.flat().map(period => ({
      label: getSettlementPeriodName(period.settlementPeriodName),
      value: period.id,
    })) ?? [];

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize
      initialStatus={status}
      onSubmit={(values, helpers) => {
        if (onSubmit) {
          onSubmit(values);
        }
      }}>
      {({ values, status, setFieldValue }) => {
        return (
          <Form className='w-full h-full '>
            <TopbarTabs>
            <TopBar
              path={backPath}
              customBackItem={customBackItem}
              title={displayMode ? values.description : undefined}
              topbarContent={
                  <div className='w-full flex justify-between items-center gap-1 '>
                    <div className=' flex w-full justify-end pr-4 items-center'>
                      {!displayMode && (
                        <FormInput.Text
                          name='description'
                          label={t('input.label.description')}
                          displayMode={displayMode}
                          autoFocus
                        />
                      )}
                      <div className='flex w-full justify-end'>
                        {displayMode ? (
                          <LabelSignatureStatus
                            status={
                              values.forSignature
                                ? SIGNATURE_TYPE.true
                                : SIGNATURE_TYPE.false
                            }
                          />
                        ) : (
                          <div className='flex w-60'>
                            <FormInput.Select
                              name='statusId'
                              label={t('input.label.status')}
                              options={isContractSigned}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div>{topbarContent}</div>
                  </div>
                }>
                <InfoBox loading={isPending}>
                  <InfoBox.Items columns={2}>
                    <FormInput.Number
                      name='amount'
                      label={t('input.label.amount')}
                      placeholder={`0 ${defaultCurrency}`}
                      suffix={` ${defaultCurrency}`}
                      displayMode={displayMode}
                    />
                    <FormInput.Number
                      name='maximumRepaymentPercentage'
                      label={t('input.label.maximumRepaymentPercentage')}
                      displayMode={displayMode}
                      placeholder='0 %'
                      suffix=' %'
                    />
                    <FormInput.Select
                      name='settlementPeriodId'
                      label={t('input.label.settlementPeriodId')}
                      options={settlementPeriodOptions}
                      onChange={option => {
                        const [name, year] = option.label.split(' ');
                        setFieldValue('settlementPeriod', {
                          name: name,
                          year: parseInt(year),
                        });
                      }}
                      value={
                        values.settlementPeriod
                          ? {
                              value: values.settlementPeriodId,
                              label: `${values.settlementPeriod.name} ${values.settlementPeriod.year}`,
                            }
                          : undefined
                      }
                      isAsync
                    />
                    <FormInput.Select
                      options={isRepaymentsFromAllContracts}
                      label={t('input.label.repaymentsFromAllContracts')}
                      name='repaymentsFromAllContracts'
                      onChange={option => {
                        setFieldValue(
                          'repaymentsFromAllContracts',
                          option.value ? true : false
                        );
                        if (!option.value) {
                          setFieldValue('repaymentsFromAllContracts', false);
                        }

                        setFieldValue('contracts', []);
                      }}
                      displayMode={displayMode}
                    />
                    {displayMode && (
                      <FormInput.Text
                        name='date'
                        type='date'
                        label={t('input.label.date')}
                        displayMode={true}
                      />
                    )}

                    {displayMode && (
                      <FormInput.Number
                        name='remainingNetAmount'
                        label={t('input.label.remainingNetAmount')}
                        displayMode={true}
                        suffix={` ${defaultCurrency}`}
                      />
                    )}
                  </InfoBox.Items>
                </InfoBox>
                <TabsNavigation
                  tabs={[
                    {
                      value: LabelTabs.repayments,
                      name: 'repayments',
                    },
                    ...(!values.repaymentsFromAllContracts
                      ? [
                          {
                            value: LabelTabs.contracts,
                            name: 'contracts',
                          },
                        ]
                      : []),
                  ]}
                  i18nextPrefix={'cash-flow'}
                  disabled={[...disableTabs]}
                />
              </TopBar>
            </TopbarTabs>
          </Form>
        );
      }}
    </Formik>
  );
};

AdvanceLabelForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'repayments', element: <RepaymentsTable /> },
    { path: 'contracts', element: <AdvanceContracts /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default AdvanceLabelForm;
